import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MapPin, Building2, Bed, Landmark, Wallet, Clock, Zap, Download } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { BENCHMARKS, BENCHMARK_ORDER } from '../data/benchmarks';
import { Reveal } from './Reveal';
import FeasibilityQuestionnaire, { calcEMI, clearFeasibilityDraft } from './FeasibilityQuestionnaire';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { BASE_PRICES, FIT_OUT_PCT, computeMetrics, runScenarios, benchmarkWealth } from '../lib/feasibilityEngine';

const API = process.env.REACT_APP_BACKEND_URL;

// ---- pricing table lives in ../lib/feasibilityEngine ----

// ---- formatters ----
const fmtINR = (n) => {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n).toFixed(0);
  let out;
  if (abs.length <= 3) out = abs;
  else {
    const head = abs.slice(0, -3);
    const tail = abs.slice(-3);
    const pairs = [];
    let h = head;
    while (h.length > 2) { pairs.unshift(h.slice(-2)); h = h.slice(0, -2); }
    if (h) pairs.unshift(h);
    out = pairs.join(',') + ',' + tail;
  }
  return `${n < 0 ? '-' : ''}₹${out}`;
};
const fmtPct = (n) => Number.isFinite(n) ? `${n.toFixed(1)}%` : '—';
const fmtYears = (n) => {
  if (!Number.isFinite(n) || n <= 0) return '—';
  const y = Math.floor(n); const m = Math.round((n - y) * 12);
  if (y === 0) return `${m} mo`;
  return m > 0 ? `${y} yr ${m} mo` : `${y} yr`;
};

// ---- Recommended model based on suite count / business type ----
const recommendModel = (q) => {
  if (!q) return 'cs33';
  const map = { 'Glamping': 'cs31', 'Boutique Resort': 'cs37', 'Airbnb': 'cs25', 'Farm Stay': 'cs33',
                'Eco Resort': 'cs37', 'Weekend Retreat': 'ss35', 'Holiday Homes': 'ss32' };
  return map[q.business_type] || 'cs33';
};

// ============ MAIN ============
export default function HospitalityEstimator() {
  const [view, setView] = useState('landing'); // landing | quiz | report
  const [questionnaire, setQuestionnaire] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);

  // Meta / Instagram Lead Ad deep-link: /hospitality#estimator?ref=<jwt>&lead_source=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref) return;
    setPrefillLoading(true);
    fetch(`${API}/api/feasibility/prefill/${ref}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => {
        const q = { ...(data.questionnaire || {}), full_name: data.full_name || '', email: data.email || '', phone: data.phone || '' };
        setQuestionnaire(q);
        setView('report');
        toast.success(`Welcome back — your assessment from ${data.lead_source.replace(/_/g, ' ')} is loaded.`);
        setTimeout(() => document.getElementById('feasibility-report')?.scrollIntoView({ behavior: 'smooth' }), 200);
      })
      .catch(async (r) => {
        const detail = (r && r.json) ? (await r.json().catch(() => ({}))).detail : null;
        toast.error(detail || 'Prefill link expired. Please start the free assessment.');
      })
      .finally(() => setPrefillLoading(false));
  }, []);

  const startQuiz = () => setView('quiz');
  const onQuizComplete = (q) => {
    setQuestionnaire(q);
    setView('report');
    setTimeout(() => document.getElementById('feasibility-report')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  return (
    <section id="estimator" data-testid="hospitality-estimator" className="scroll-mt-32 bg-[#F5F5F5] py-32 md:py-40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Landing show={view === 'landing' && !prefillLoading} onStart={startQuiz} />

        {view === 'quiz' && (
          <div data-testid="feasibility-quiz-wrap">
            <div className="text-center mb-10 md:mb-14">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Investment Assessment</div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight">A few quick questions.</h2>
            </div>
            <FeasibilityQuestionnaire onComplete={onQuizComplete} />
          </div>
        )}

        {view === 'report' && <Report questionnaire={questionnaire} onReset={() => { clearFeasibilityDraft(); setQuestionnaire(null); setView('landing'); }} />}
      </div>
    </section>
  );
}

// ---------- Landing ----------
const Landing = ({ show, onStart }) => {
  if (!show) return null;
  return (
    <Reveal>
      <div className="text-center max-w-4xl mx-auto py-8 md:py-16" data-testid="feasibility-landing">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Investment Feasibility Report</div>
        <h2 className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.0]">
          Thinking of setting up a<br/><span className="italic text-black/40">hospitality business?</span>
        </h2>
        <p className="mt-8 max-w-2xl mx-auto text-[15px] md:text-[17px] text-black/60 leading-[1.75]">
          Go prefab with MODULAROOM. Discover your project&apos;s investment potential with our personalised
          Investment Feasibility Report — tailored to your land, financing and business model.
        </p>
        <button
          type="button"
          onClick={onStart}
          data-testid="start-assessment"
          className="mt-10 inline-flex items-center gap-3 rounded-full bg-[#111] text-white px-9 py-4 text-[12px] tracking-[0.24em] uppercase hover:bg-[#C7A36A] transition-colors"
        >Start Free Assessment →</button>
        <div className="mt-6 text-[11px] tracking-[0.18em] uppercase text-black/40">2 minutes · Fully personalised · Free</div>
      </div>
    </Reveal>
  );
};

// ---------- Report (calculator + full feasibility report) ----------
const KPI = ({ label, value, hint, accent, testid }) => (
  <div data-testid={testid} className={`p-6 md:p-7 border ${accent ? 'bg-[#111] text-white border-[#111]' : 'bg-white text-[#111] border-black/10'}`}>
    <div className={`text-[10px] tracking-[0.32em] uppercase ${accent ? 'text-[#C7A36A]' : 'text-black/45'}`}>{label}</div>
    <div className="mt-4 font-display text-2xl md:text-[30px] leading-[1.05] tracking-tight tabular-nums break-words">{value}</div>
    {hint && <div className={`mt-2 text-[12px] ${accent ? 'text-white/55' : 'text-black/50'}`}>{hint}</div>}
  </div>
);

const Report = ({ questionnaire, onReset }) => {
  const q = questionnaire || {};
  const [modelSlug, setModelSlug] = useState(recommendModel(q));
  const [units, setUnits] = useState(Math.min(20, q.suites_count || 5));
  const [adr, setAdr] = useState(15000);
  const [occupancy, setOccupancy] = useState(65);
  const [siteDevCost, setSiteDevCost] = useState(Number(q.site_development_cost) || 0);
  const [includeLandApp, setIncludeLandApp] = useState(true);
  const [landAppRate, setLandAppRate] = useState(6);   // %/yr
  const [bldgDepRate, setBldgDepRate] = useState(1.5); // %/yr (net depreciation on modules)
  // Lease inputs (only if leasing)
  const [monthlyLease, setMonthlyLease] = useState(50000);
  const [leaseEsc, setLeaseEsc] = useState(5);
  const [secDeposit, setSecDeposit] = useState(500000);
  const [leaseYears, setLeaseYears] = useState(15);
  // Land estimate for wealth projection (if owned) — sensible default
  const [landValue, setLandValue] = useState(5000000);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const reportRef = useRef(null);

  const model = PRODUCTS.find((p) => p.slug === modelSlug) || PRODUCTS[0];
  const basePrice = BASE_PRICES[model.slug] || 0;
  const isLease = q.land_ownership === 'lease';
  const isOwned = q.land_ownership === 'owned';
  const financePlan = q.financing_plan;
  const emi = useMemo(() => calcEMI(q), [q]);

  // Additional editable calculator inputs
  const [maintPct, setMaintPct] = useState(5);
  const [opexPct, setOpexPct] = useState(10);

  // Single input snapshot fed into the shared engine (used by KPIs, scenarios, benchmarks).
  const engineInput = useMemo(() => ({
    model_slug: modelSlug, units, adr, occupancy,
    land_ownership: q.land_ownership, financing_plan: q.financing_plan,
    finance_amount: q.finance_amount, interest_rate: q.interest_rate,
    finance_tenure_years: q.finance_tenure_years, down_payment_pct: q.down_payment_pct,
    monthly_lease: isLease ? monthlyLease : 0,
    security_deposit: isLease ? secDeposit : 0,
    include_land_appreciation: includeLandApp,
    annual_land_appreciation: landAppRate,
    annual_building_depreciation: bldgDepRate,
    land_value: landValue,
    maint_pct: maintPct / 100,
    opex_pct: opexPct / 100,
    horizon_years: 10,
    site_development_cost: siteDevCost,
  }), [modelSlug, units, adr, occupancy, q, isLease, monthlyLease, secDeposit, includeLandApp, landAppRate, bldgDepRate, landValue, maintPct, opexPct, siteDevCost]);

  const metrics = useMemo(() => computeMetrics(engineInput), [engineInput]);
  const scenarios = useMemo(() => runScenarios(engineInput), [engineInput]);

  const chartData = useMemo(() => Array.from({ length: 10 }, (_, i) => ({
    year: `Y${i + 1}`,
    Conservative: Math.round(scenarios[0].metrics.annualProfit * (i + 1)),
    Expected:     Math.round(scenarios[1].metrics.annualProfit * (i + 1)),
    Optimistic:   Math.round(scenarios[2].metrics.annualProfit * (i + 1)),
  })), [scenarios]);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/feasibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: q.full_name, email: q.email, phone: q.phone,
          questionnaire: {
            land_ownership: q.land_ownership, location_scope: q.location_scope,
            location_country: q.location_country, location_state: q.location_state, location_city: q.location_city,
            business_type: q.business_type, business_type_other: q.business_type_other,
            suites_count: q.suites_count, financing_plan: q.financing_plan,
            down_payment_pct: q.down_payment_pct, finance_amount: q.finance_amount,
            interest_rate: q.interest_rate, finance_tenure_years: q.finance_tenure_years,
            utilities: q.utilities, timeline: q.timeline,
          },
          inputs: {
            model_slug: modelSlug, units, adr, occupancy,
            site_development_cost: siteDevCost,
            include_land_appreciation: includeLandApp, annual_land_appreciation: landAppRate,
            annual_building_depreciation: bldgDepRate,
            monthly_lease: isLease ? monthlyLease : null,
            annual_lease_escalation: isLease ? leaseEsc : null,
            security_deposit: isLease ? secDeposit : null,
            lease_duration_years: isLease ? leaseYears : null,
          },
          outputs: {
            total_investment: metrics.totalInvestment,
            annual_revenue: metrics.annualRevenue,
            annual_profit: metrics.annualProfit,
            monthly_cash_flow: metrics.monthlyCashFlow,
            roi_pct: metrics.roi,
            payback_years: Number.isFinite(metrics.payback) ? metrics.payback : null,
            emi_monthly: emi,
            wealth_10y: metrics.wealth,
          },
          lead_source: new URLSearchParams(window.location.search).get('lead_source') || 'website',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSubmitted(true);
      clearFeasibilityDraft();
      toast.success(data.email_sent ? "Report emailed to you. Check your inbox." : "Report saved. We'll be in touch.");
    } catch (e) {
      toast.error('Could not send the report. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    setPdfBusy(true);
    try {
      // Lazy-load to keep the initial bundle small (html2pdf is ~500KB).
      const html2pdf = (await import('html2pdf.js')).default;
      const filename = `MODULAROOM-Feasibility-${(q.full_name || 'Report').replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
      const opts = {
        margin:       [10, 10, 12, 10],
        filename,
        image:        { type: 'jpeg', quality: 0.94 },
        html2canvas:  { scale: 2, useCORS: true, backgroundColor: '#F5F5F5', windowWidth: 1200 },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
        pagebreak:    { mode: ['css', 'legacy'], avoid: ['[data-pdf-avoid-break]', 'tr', '.recharts-wrapper'] },
      };
      // Temporarily add a class so we can hide non-print elements via CSS.
      reportRef.current.classList.add('pdf-exporting');
      await html2pdf().set(opts).from(reportRef.current).save();
      toast.success('Feasibility PDF downloaded.');
    } catch (err) {
      toast.error('Could not generate PDF. Please try again.');
    } finally {
      if (reportRef.current) reportRef.current.classList.remove('pdf-exporting');
      setPdfBusy(false);
    }
  };

  const location = [q.location_city, q.location_state, q.location_country].filter(Boolean).join(', ') || '—';
  const biz = q.business_type === 'Other' ? q.business_type_other : q.business_type;

  return (
    <div id="feasibility-report" ref={reportRef} data-testid="feasibility-report" className="space-y-14">
      {/* Report header */}
      <Reveal>
        <div className="max-w-3xl">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Investment Feasibility Report</div>
          <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[1.02]">
            Prepared for <span className="italic text-black/50">{q.full_name || 'you'}.</span>
          </h2>
          <p className="mt-6 text-[15px] text-black/60 leading-relaxed">
            Based on the assumptions you shared, here is an indicative feasibility model for a
            MODULAROOM-powered {biz || 'hospitality project'}{location !== '—' ? ` in ${location}` : ''}.
            Adjust any assumption below to see the numbers update live.
          </p>
        </div>
      </Reveal>

      {/* Your Assumptions — icon-driven recap */}
      <Reveal>
        <AssumptionsRecap q={q} location={location} biz={biz} units={units} adr={adr} occupancy={occupancy} />
      </Reveal>

      {/* Project summary */}
      <Reveal>
        <SectionCard title="01 · Project Summary" testid="report-summary">
          <SummaryGrid rows={[
            ['Location', location],
            ['Business type', biz || '—'],
            ['Suites planned', String(q.suites_count || '—')],
            ['Land status', q.land_ownership === 'owned' ? 'Owned' : q.land_ownership === 'lease' ? 'Leased' : 'Searching'],
            ['Financing plan', q.financing_plan === 'self' ? 'Self-funded' : q.financing_plan === 'finance' ? 'Financed' : q.financing_plan === 'mixed' ? 'Combination' : '—'],
            ['Timeline', q.timeline || '—'],
          ]} />
          {isOwned && (
            <div className="mt-6 p-4 bg-[#F5F5F5] text-[13px] text-black/70 leading-relaxed">
              Land acquisition cost is excluded because you already own the land. Long-term land appreciation
              may optionally be included in wealth projections below.
            </div>
          )}
        </SectionCard>
      </Reveal>

      {/* Editable calculator inputs */}
      <Reveal>
        <SectionCard title="02 · Investment Assumptions" testid="report-assumptions">
          {/* Modularoom Investment */}
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-4">Modularoom Investment</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
            {PRODUCTS.filter((p) => BASE_PRICES[p.slug]).map((p) => (
              <button
                key={p.slug}
                onClick={() => setModelSlug(p.slug)}
                data-testid={`report-model-${p.slug}`}
                className={`p-3 text-left text-[12px] border transition-colors ${modelSlug === p.slug ? 'bg-[#111] text-white border-[#111]' : 'bg-white border-black/10 hover:border-black/40'}`}
              >
                <div className="font-mono tracking-[0.1em]">{p.name}</div>
                <div className={`mt-1 text-[10px] ${modelSlug === p.slug ? 'text-[#C7A36A]' : 'text-black/50'}`}>{fmtINR(BASE_PRICES[p.slug])}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 text-[12px] text-black/50">Selected model &amp; unit count define your Modularoom investment (product + fit-out).</div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <FieldNum label="Units" value={units} onChange={(v) => setUnits(Math.max(1, Math.round(v)))} suffix="units" testid="report-units" />
            <FieldNum label="Room rate" value={adr} onChange={setAdr} prefix="₹" suffix="/night" step={500} testid="report-adr" />
            <FieldNum label="Occupancy" value={occupancy} onChange={(v) => setOccupancy(Math.max(0, Math.min(100, v)))} suffix="%" testid="report-occ" />
            <FieldNum label="Maintenance" value={maintPct} onChange={setMaintPct} suffix="% of revenue" step={0.5} testid="report-maint" />
            <FieldNum label="Operating costs" value={opexPct} onChange={setOpexPct} suffix="% of revenue" step={0.5} testid="report-opex" />
          </div>

          {/* Project Development (separate capital line) */}
          <div className="mt-10 pt-8 border-t border-black/10">
            <div className="flex items-baseline justify-between flex-wrap gap-4">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Project Development</div>
              <div className="text-[11px] text-black/45">Separate from your Modularoom unit cost.</div>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
              <FieldNum
                label="Site Development Cost"
                value={siteDevCost}
                onChange={(v) => setSiteDevCost(Math.max(0, Math.round(Number(v) || 0)))}
                prefix="₹"
                step={100000}
                testid="report-site-dev"
              />
              <div className="text-[12px] leading-[1.7] text-black/60 sm:pt-6">
                Site preparation, foundations, utilities, landscaping and external works. Varies by site and scope. Enter your estimate when available — leave at ₹0 until then.
              </div>
            </div>
          </div>
        </SectionCard>
      </Reveal>

      {/* Investment breakdown — every capital line, counted exactly once */}
      <Reveal>
        <SectionCard title="Investment Breakdown" testid="report-investment-breakdown">
          <div className="border-t border-black/10">
            <BreakdownRow label="Modularoom Units" hint={`${units} × ${fmtINR(basePrice)}`} value={fmtINR(metrics.modulesTotal)} testid="bd-modules" />
            <BreakdownRow label="Fit-out" hint={`${Math.round(FIT_OUT_PCT * 100)}% of unit cost`} value={fmtINR(metrics.fitOutTotal)} testid="bd-fitout" />
            <BreakdownRow label="Site Development" hint={siteDevCost > 0 ? 'Project-specific capital cost' : 'Not entered yet'} value={fmtINR(metrics.siteDevTotal)} testid="bd-sitedev" muted={metrics.siteDevTotal === 0} />
            {isLease && (
              <BreakdownRow label="Security Deposit" hint="Refundable" value={fmtINR(Number(secDeposit) || 0)} testid="bd-secdep" />
            )}
            <BreakdownRow label="Total Project Investment" value={fmtINR(metrics.totalInvestment)} testid="bd-total" accent />
          </div>
          <p className="mt-5 text-[12px] leading-[1.7] text-black/55">
            Site development is project-specific and can vary significantly depending on location, terrain, utilities, infrastructure, approvals and scope.
          </p>
        </SectionCard>
      </Reveal>

      {/* Lease section */}
      {isLease && (
        <Reveal>
          <SectionCard title="03 · Lease Terms" testid="report-lease">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <FieldNum label="Monthly lease" value={monthlyLease} onChange={setMonthlyLease} prefix="₹" step={5000} testid="report-lease-monthly" />
              <FieldNum label="Annual escalation" value={leaseEsc} onChange={setLeaseEsc} suffix="%" step={0.5} testid="report-lease-esc" />
              <FieldNum label="Security deposit" value={secDeposit} onChange={setSecDeposit} prefix="₹" step={50000} testid="report-lease-dep" />
              <FieldNum label="Lease duration" value={leaseYears} onChange={setLeaseYears} suffix="years" step={1} testid="report-lease-years" />
            </div>
          </SectionCard>
        </Reveal>
      )}

      {/* Financing summary */}
      {(financePlan === 'finance' || financePlan === 'mixed') && (
        <Reveal>
          <SectionCard title={`${isLease ? '04' : '03'} · Financing Summary`} testid="report-financing">
            <SummaryGrid rows={[
              ['Plan', financePlan === 'finance' ? 'Financed' : 'Combination'],
              ['Down payment', fmtPct(Number(q.down_payment_pct))],
              ['Finance amount', fmtINR(Number(q.finance_amount || 0))],
              ['Interest rate', fmtPct(Number(q.interest_rate))],
              ['Tenure', `${q.finance_tenure_years} years`],
              ['Estimated EMI', `${fmtINR(emi)} / month`],
            ]} />
          </SectionCard>
        </Reveal>
      )}

      {/* KPIs */}
      <Reveal>
        <SectionCard title={`${(isLease && (financePlan === 'finance' || financePlan === 'mixed')) ? '05' : (isLease || financePlan === 'finance' || financePlan === 'mixed') ? '04' : '03'} · Feasibility Outputs`} testid="report-kpis">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <KPI accent label="Total Project Investment" value={fmtINR(metrics.totalInvestment)} hint={`Modules ${fmtINR(metrics.modulesTotal)} + Fit-out ${fmtINR(metrics.fitOutTotal)}${metrics.siteDevTotal > 0 ? ` + Site Dev ${fmtINR(metrics.siteDevTotal)}` : ''}`} testid="kpi-total-investment" />
            <KPI label="Annual Revenue" value={fmtINR(metrics.annualRevenue)} hint={`${occupancy}% × 365 days`} testid="kpi-annual-revenue" />
            <KPI label="Annual Profit" value={fmtINR(metrics.annualProfit)} hint="Net of maintenance, lease and EMI" testid="kpi-annual-profit" />
            <KPI label="Monthly Cash Flow" value={fmtINR(metrics.monthlyCashFlow)} hint={metrics.monthlyCashFlow >= 0 ? 'Positive' : 'Below breakeven'} testid="kpi-monthly-cashflow" />
            <KPI label="Projected ROI" value={fmtPct(metrics.roi)} hint="Annualised" testid="kpi-roi" />
            <KPI label="Payback Period" value={fmtYears(metrics.payback)} hint="At current assumptions" testid="kpi-payback" />
            <KPI label={<span title="IRR — the annualised return that makes the net present value of all cash flows (initial investment, annual profit and terminal asset value) equal to zero.">IRR ⓘ</span>} value={metrics.irrPct == null ? '—' : fmtPct(metrics.irrPct)} hint="10-year Newton–Raphson" testid="kpi-irr" />
            <KPI label={<span title="Cash-on-Cash Return — annual profit divided by the actual equity you deployed (down payment + security deposit), not total project cost.">Cash-on-Cash ⓘ</span>} value={fmtPct(metrics.cashOnCash)} hint={`Equity ${fmtINR(metrics.equityDeployed)}`} testid="kpi-coc" />
            <KPI label="Operating Costs" value={fmtINR(metrics.annualMaint + metrics.annualOpex)} hint={`Maint ${maintPct}% + Opex ${opexPct}%`} testid="kpi-opcosts" />
          </div>
        </SectionCard>
      </Reveal>

      {/* Wealth projection */}
      <Reveal>
        <SectionCard title="Long-Term Wealth Projection" testid="report-wealth">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <label className="inline-flex items-center gap-3 text-[13px] text-black/70">
              <input type="checkbox" checked={includeLandApp} onChange={(e) => setIncludeLandApp(e.target.checked)} data-testid="toggle-land-app" className="accent-[#C7A36A] w-4 h-4" />
              Include land appreciation (owned land)
            </label>
            <div className="flex gap-6 flex-wrap">
              <FieldNum inline label="Annual land appreciation" value={landAppRate} onChange={setLandAppRate} suffix="%" step={0.5} testid="land-app-rate" />
              <FieldNum inline label="Annual building depreciation" value={bldgDepRate} onChange={setBldgDepRate} suffix="%" step={0.5} testid="bldg-dep-rate" />
              {isOwned && includeLandApp && (
                <FieldNum inline label="Est. current land value" value={landValue} onChange={setLandValue} prefix="₹" step={100000} testid="land-value" />
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI label="Land Value (10 yr)" value={isOwned && includeLandApp ? fmtINR(metrics.landAppr) : '—'} testid="kpi-land-10y" />
            <KPI label="Building Value (10 yr)" value={fmtINR(metrics.bldgVal)} testid="kpi-bldg-10y" />
            <KPI label="Cumulative Profit (10 yr)" value={fmtINR(metrics.annualProfit * 10)} testid="kpi-cum-10y" />
            <KPI accent label="Total Wealth (10 yr)" value={fmtINR(metrics.wealth)} testid="kpi-wealth-10y" />
          </div>
          <div className="mt-6 h-[240px] w-full bg-white border border-black/10 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gWealth" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C7A36A" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#C7A36A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#00000010" vertical={false} />
                <XAxis dataKey="year" tick={{ fill: '#00000080', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#00000015' }} />
                <YAxis tickFormatter={(v) => fmtINR(v)} tick={{ fill: '#00000080', fontSize: 11 }} tickLine={false} axisLine={false} width={110} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: '#111', border: 'none', color: '#fff', fontSize: 12 }} labelStyle={{ color: '#C7A36A' }} />
                <Area type="monotone" dataKey="Wealth" stroke="#C7A36A" strokeWidth={2} fill="url(#gWealth)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </Reveal>

      {/* Scenario Analysis */}
      <Reveal>
        <SectionCard title="Scenario Analysis" testid="report-scenarios">
          <p className="text-[13px] text-black/55 mb-6 max-w-2xl">
            Occupancy is the single biggest driver of hospitality returns. These three scenarios keep every
            other assumption identical and adjust occupancy to model downside, expected and upside performance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {scenarios.map((s, i) => (
              <div key={s.key} data-testid={`scenario-${s.key}`} className={`p-6 border ${i === 1 ? 'bg-[#111] text-white border-[#111]' : 'bg-white border-black/10'}`}>
                <div className={`flex items-baseline justify-between ${i === 1 ? 'text-[#C7A36A]' : 'text-black/45'}`}>
                  <span className="text-[10px] tracking-[0.32em] uppercase">{s.label}</span>
                  <span className="text-[11px]">{s.occupancy}% occ.</span>
                </div>
                <div className="mt-5 space-y-2.5 text-[13px]">
                  <ScenRow label="Monthly Revenue" v={fmtINR(s.metrics.monthlyRevenue)} accent={i === 1} />
                  <ScenRow label="Monthly Cash Flow" v={fmtINR(s.metrics.monthlyCashFlow)} accent={i === 1} />
                  <ScenRow label="Annual Profit" v={fmtINR(s.metrics.annualProfit)} accent={i === 1} />
                  <ScenRow label="ROI" v={fmtPct(s.metrics.roi)} accent={i === 1} />
                  <ScenRow label="IRR" v={s.metrics.irrPct == null ? '—' : fmtPct(s.metrics.irrPct)} accent={i === 1} />
                  <ScenRow label="Cash-on-Cash" v={fmtPct(s.metrics.cashOnCash)} accent={i === 1} />
                  <ScenRow label="Payback" v={fmtYears(s.metrics.payback)} accent={i === 1} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 h-[260px] w-full bg-white border border-black/10 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#00000010" vertical={false} />
                <XAxis dataKey="year" tick={{ fill: '#00000080', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#00000015' }} />
                <YAxis tickFormatter={(v) => fmtINR(v)} tick={{ fill: '#00000080', fontSize: 11 }} tickLine={false} axisLine={false} width={110} />
                <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: '#111', border: 'none', color: '#fff', fontSize: 12 }} labelStyle={{ color: '#C7A36A' }} />
                <Legend wrapperStyle={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' }} />
                <Line type="monotone" dataKey="Conservative" stroke="#888888" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="Expected" stroke="#C7A36A" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="Optimistic" stroke="#111111" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </Reveal>

      {/* Compare with Other Investments */}
      <BenchmarkSection totalInvestment={metrics.totalInvestment} modularoomWealth={metrics.wealth} />

      {/* Disclaimer + Submit */}
      <Reveal>
        <div className="border-t border-black/10 pt-8 max-w-3xl">
          <div className="text-[10px] tracking-[0.32em] uppercase text-black/45 mb-3">Disclaimer</div>
          <p className="text-[13px] leading-relaxed text-black/55">
            The Investment Feasibility Report provides <em>illustrative projections only</em>. Figures are
            indicative and do not constitute financial advice. Actual results depend on market conditions,
            location, seasonality, operating costs and financing structure.
          </p>
        </div>
      </Reveal>

      {/* CTA */}
      <div className="bg-[#111] text-white -mx-6 md:-mx-12 px-6 md:px-12 py-16 md:py-24 pdf-hide">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Send my report</div>
            <h3 className="mt-4 font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
              Email me the full<br/><span className="italic text-white/50">feasibility report.</span>
            </h3>
            <p className="mt-5 max-w-2xl text-white/60 text-[15px] leading-relaxed">
              We&apos;ll email a branded copy of your report to <span className="text-white">{q.email}</span> and
              share your assumptions with a MODULAROOM specialist for a personalised follow-up.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end flex flex-wrap items-center gap-3">
            <button
              onClick={downloadPdf}
              disabled={pdfBusy}
              data-testid="download-feasibility-pdf"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-6 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-white hover:text-[#111] transition-colors disabled:opacity-40"
            >
              <Download size={14} strokeWidth={1.5} />
              {pdfBusy ? 'Preparing…' : 'Download PDF'}
            </button>
            {!submitted ? (
              <button onClick={submit} disabled={busy} data-testid="submit-feasibility" className="inline-flex items-center gap-2 rounded-full bg-white text-[#111] px-7 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-[#C7A36A] hover:text-white transition-colors disabled:opacity-40">
                {busy ? 'Sending…' : 'Email my report →'}
              </button>
            ) : (
              <Link to="/contact#book" className="inline-flex items-center gap-2 rounded-full bg-[#C7A36A] text-white px-7 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-white hover:text-[#111] transition-colors">Book consultation →</Link>
            )}
            <button onClick={onReset} data-testid="reset-feasibility" className="text-[11px] tracking-[0.22em] uppercase text-white/60 hover:text-white transition-colors">Start over</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Small building blocks ----------
const AssumptionsRecap = ({ q, location, biz, units, adr, occupancy }) => {
  const items = [
    { icon: MapPin,    label: 'Location',      value: location },
    { icon: Building2, label: 'Business type', value: biz || 'Not specified' },
    { icon: Bed,       label: 'Suites',        value: `${units} planned` },
    { icon: Landmark,  label: 'Land',          value: q.land_ownership === 'owned' ? 'Owned' : q.land_ownership === 'lease' ? 'Leased' : 'Searching' },
    { icon: Wallet,    label: 'Financing',     value: q.financing_plan === 'self' ? 'Self-funded' : q.financing_plan === 'finance' ? 'Financed' : q.financing_plan === 'mixed' ? 'Combination' : '—' },
    { icon: Clock,     label: 'Timeline',      value: q.timeline || '—' },
    { icon: Zap,       label: 'Room rate',     value: `${fmtINR(adr)} · ${occupancy}% occ.` },
    { icon: Building2, label: 'Utilities',     value: (q.utilities || []).length ? `${(q.utilities || []).length} available` : 'None specified' },
  ];
  return (
    <div data-testid="assumptions-recap" data-pdf-avoid-break className="bg-white border border-black/10 p-6 md:p-9">
      <div className="flex items-baseline justify-between gap-6 flex-wrap mb-6">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Your Assumptions</div>
        <div className="text-[11px] text-black/45">Projections below are personalised to these inputs.</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6">
        {items.map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-start gap-3">
            <div className="mt-0.5 shrink-0 w-9 h-9 rounded-full bg-[#F5F0E8] text-[#C7A36A] flex items-center justify-center">
              <Icon size={16} strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.24em] uppercase text-black/45">{label}</div>
              <div className="mt-1 text-[14px] text-black/85 leading-snug break-words">{value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, testid }) => (
  <div data-testid={testid} className="bg-white border border-black/10 p-6 md:p-10">
    <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-6">{title}</div>
    {children}
  </div>
);

const SummaryGrid = ({ rows }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
    {rows.map(([k, v]) => (
      <div key={k} className="flex items-baseline justify-between border-b border-black/10 py-2">
        <span className="text-[10px] tracking-[0.28em] uppercase text-black/45">{k}</span>
        <span className="text-[14px] text-black/85">{v}</span>
      </div>
    ))}
  </div>
);

const BreakdownRow = ({ label, hint, value, testid, accent, muted }) => (
  <div
    data-testid={testid}
    className={`grid grid-cols-[1fr_auto] gap-6 items-baseline py-4 border-b border-black/10 ${accent ? 'bg-[#FAF6EE] px-4 -mx-4 border-t-2 border-t-[#C7A36A]' : ''}`}
  >
    <div>
      <div className={`text-[10px] tracking-[0.28em] uppercase ${accent ? 'text-[#C7A36A]' : 'text-black/50'}`}>{label}</div>
      {hint && <div className={`mt-1 text-[11px] ${muted ? 'text-black/35 italic' : 'text-black/45'}`}>{hint}</div>}
    </div>
    <div className={`text-right tabular-nums ${accent ? 'text-[20px] md:text-[22px] text-black/90 font-medium' : muted ? 'text-[15px] text-black/50' : 'text-[15px] text-black/85'}`}>{value}</div>
  </div>
);

const FieldNum = ({ label, value, onChange, prefix, suffix, step = 1, inline, testid }) => (
  <label className={inline ? 'block min-w-[180px]' : 'block'}>
    <div className="text-[10px] tracking-[0.28em] uppercase text-black/45 mb-2">{label}</div>
    <div className="flex items-center border-b border-black/15 focus-within:border-[#C7A36A]">
      {prefix && <span className="pr-2 text-black/50 text-[15px]">{prefix}</span>}
      <input
        type="number"
        data-testid={testid}
        value={Number.isFinite(value) ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        step={step}
        inputMode="decimal"
        className="w-full bg-transparent py-2.5 text-[16px] focus:outline-none tabular-nums"
      />
      {suffix && <span className="pl-2 text-black/50 text-[13px]">{suffix}</span>}
    </div>
  </label>
);

const ScenRow = ({ label, v, accent }) => (
  <div className={`flex items-baseline justify-between border-b py-1.5 ${accent ? 'border-white/10' : 'border-black/10'}`}>
    <span className={`text-[10px] tracking-[0.22em] uppercase ${accent ? 'text-white/60' : 'text-black/50'}`}>{label}</span>
    <span className={`text-[14px] tabular-nums ${accent ? 'text-white' : 'text-black/85'}`}>{v}</span>
  </div>
);

// ---------- Compare with Other Investments ----------
function BenchmarkSection({ totalInvestment, modularoomWealth }) {
  const [rates, setRates] = useState(() => Object.fromEntries(Object.keys(BENCHMARKS).map((k) => [k, BENCHMARKS[k].defaultReturnPct])));
  const [edited, setEdited] = useState(false);
  const setRate = (k, v) => { setRates((r) => ({ ...r, [k]: v })); setEdited(true); };
  const wealthOf = (k) => benchmarkWealth(totalInvestment, rates[k], 10);
  const bars = [
    ...BENCHMARK_ORDER.map((k) => ({ name: BENCHMARKS[k].label, value: Math.round(wealthOf(k)) })),
    { name: 'MODULAROOM', value: Math.round(modularoomWealth) },
  ];

  return (
    <Reveal>
      <SectionCard title="Compare with Other Investments" testid="report-benchmarks">
        <p className="text-[13px] text-black/55 mb-6 max-w-3xl">
          The same {fmtINR(totalInvestment)} allocated to alternative asset classes, projected over 10 years.
          Default return assumptions come from published research — edit any value to see the impact instantly.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENCHMARK_ORDER.map((k) => {
            const b = BENCHMARKS[k];
            return (
              <div key={k} className="p-5 bg-white border border-black/10" data-testid={`bench-card-${k}`}>
                <div className="text-[10px] tracking-[0.32em] uppercase text-black/45">{b.label}</div>
                <div className="mt-4">
                  <FieldNum label={`Annual return (${edited ? 'user' : 'published default'})`} value={rates[k]} onChange={(v) => setRate(k, v)} suffix="%" step={0.1} testid={`bench-rate-${k}`} />
                </div>
                <div className="mt-5 text-[10px] tracking-[0.22em] uppercase text-black/40">Wealth in 10 yr</div>
                <div className="mt-1 font-display text-2xl tracking-tight tabular-nums" data-testid={`bench-wealth-${k}`}>{fmtINR(wealthOf(k))}</div>
                <div className="mt-4 pt-3 border-t border-black/10 text-[11px] leading-relaxed text-black/50">
                  <div>Source: <a href={b.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#C7A36A]">{b.source}</a></div>
                  <div className="mt-1">Published {b.publicationDate} · Last updated {b.lastUpdated}</div>
                </div>
              </div>
            );
          })}
          <div className="p-5 bg-[#111] text-white border border-[#111]" data-testid="bench-card-modularoom">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">MODULAROOM Hospitality</div>
            <div className="mt-4 text-[12px] text-white/60">Uses your own project assumptions above.</div>
            <div className="mt-5 text-[10px] tracking-[0.22em] uppercase text-white/45">Wealth in 10 yr</div>
            <div className="mt-1 font-display text-2xl tracking-tight tabular-nums text-[#C7A36A]" data-testid="bench-wealth-modularoom">{fmtINR(modularoomWealth)}</div>
            <div className="mt-4 pt-3 border-t border-white/10 text-[11px] leading-relaxed text-white/55">
              Includes cumulative operating profit, land appreciation (if owned + toggled on) and residual building value.
            </div>
          </div>
        </div>
        <div className="mt-8 h-[260px] w-full bg-white border border-black/10 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#00000010" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: '#00000080', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#00000015' }} />
              <YAxis tickFormatter={(v) => fmtINR(v)} tick={{ fill: '#00000080', fontSize: 11 }} tickLine={false} axisLine={false} width={110} />
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ background: '#111', border: 'none', color: '#fff', fontSize: 12 }} labelStyle={{ color: '#C7A36A' }} cursor={{ fill: '#00000008' }} />
              <Bar dataKey="value" fill="#C7A36A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-5 flex items-baseline justify-between gap-4 text-[11px] text-black/50">
          <span>Assumptions in use: <span className={edited ? 'text-[#C7A36A]' : 'text-black/70'}>{edited ? 'User-modified' : 'Published defaults'}</span></span>
          {edited && <button onClick={() => { setRates(Object.fromEntries(Object.keys(BENCHMARKS).map((k) => [k, BENCHMARKS[k].defaultReturnPct]))); setEdited(false); }} className="tracking-[0.22em] uppercase hover:text-[#C7A36A] transition-colors" data-testid="bench-reset">Reset to defaults</button>}
        </div>
        <div className="mt-4 text-[11px] text-black/45 italic">
          Past performance and published market averages are provided for illustrative purposes only and do not guarantee future returns.
        </div>
      </SectionCard>
    </Reveal>
  );
}
